import { createFileRoute, Link } from '@tanstack/react-router';
import { productIdPathParams } from '@/lib/product-url-codec';
import { z } from 'zod';
import { CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AgentRunCard } from '@/components/AgentRunCard';
import { AgentPollingPanel } from '@/components/AgentPollingPanel';
import { formatDuration, formatServerDateShort, parseServerDate } from '@/lib/dates';
import { useMe } from '@/features/auth/hooks';
import {
  useClinicalProtocolDispatchDetail,
  useClinicalProtocolRun,
} from '@/features/clinical-protocol/hooks';
import type { ClinicalProtocolRunDetail } from '@/features/clinical-protocol/api';
import { ClinicalProtocolResultRenderer } from '@/features/clinical-protocol/components/ClinicalProtocolResultRenderer';
import { ClinicalProtocolDispatchForm } from '@/features/clinical-protocol/components/ClinicalProtocolDispatchForm';
import { ProtocolStaleBanner } from '@/features/clinical-protocol/components/ProtocolStaleBanner';
import { useProducts } from '@/features/product/hooks';
import { PageHero } from '@/components/layout/PageHero';
import { ScrollToTopButton } from '@/components/ScrollToTopButton';
import { DispatchTabsShell } from '@/features/agent-dispatch/DispatchTabsShell';
import { QueuedBanner } from '@/features/agent-dispatch/QueuedBanner';
import { FailedBanner as DispatchFailedBanner } from '@/features/agent-dispatch/FailedBanner';
import { countryLabel } from '@/features/agent-dispatch/countryLabel';
import type { DispatchData } from '@/features/agent-dispatch/types';
import { RunLoadingSkeleton } from '@/components/RunLoadingSkeleton';

/**
 * Clinical Protocols result page.
 *
 * URL: /clinical-protocols/:productId?protocol_run_id=<n>
 *
 * Polls the protocol run every poll_ms (server-tuned, ~3s default). Shows:
 *   - Run-identity strip
 *   - In-progress workflow stepper (clinical-protocol specific steps)
 *   - Failed banner with friendly error copy
 *   - Completed: confirmation + ClinicalProtocolResultRenderer + disclaimer
 */

const searchSchema = z.object({
  protocol_run_id: z.coerce.number().int().positive().optional().catch(undefined),
});

const PROTOCOL_WORKFLOW_STEPS = [
  'Read product specifications',
  'Query ClinicalTrials.gov for precedents',
  'Consult regulatory guidance documents',
  'Design study endpoints',
  'Draft inclusion/exclusion criteria',
  'Compose statistical plan',
  'Finalize protocol',
] as const;

const PROTOCOL_STEP_SECONDS = 14;

export const Route = createFileRoute('/clinical-protocols_/$productId')({
  // Opaque token in the address bar, never the global product id.
  params: productIdPathParams,
  validateSearch: searchSchema,
  component: ClinicalProtocolResultPage,
});

function ClinicalProtocolResultPage() {
  const { productId } = Route.useParams();
  const { protocol_run_id: runId } = Route.useSearch();
  const { data: run, isLoading, isError } = useClinicalProtocolRun(runId ?? null);

  return (
    <>
      <PageHero
        title="Clinical Protocol"
        subtitle="A regulator-aligned clinical protocol for this product — inclusion/exclusion criteria, endpoints, statistical plan, and safety monitoring — grounded in trial-registry precedents and current regulatory guidance via the REGARA AI agent."
      />

      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pt-7 pb-20">
        {runId === undefined && <DispatchOrMissing productId={productId} />}
        {runId !== undefined && isLoading && (
          <RunLoadingSkeleton />
        )}
        {runId !== undefined && isError && (
          <ErrorCard message="Could not load the run. Please refresh." />
        )}
        {runId !== undefined && run && (
          run.dispatch_group_id ? (
            // ``initialRunId`` opens the tab shell ON the run the URL
            // targeted, not the first sibling (same fix as the strategy and
            // predicate result pages).
            <DispatchView groupId={run.dispatch_group_id} initialRunId={run.id} />
          ) : (
            <>
              <AgentRunCard run={run} agentLabel="Protocol run" />
              <RunStateBody run={run} />
              {run.status === 'completed' && <ResultDisclaimer country={run.country} />}
            </>
          )
        )}
      </main>
      <ScrollToTopButton />
    </>
  );
}

function RunStateBody({ run, country }: { run: ClinicalProtocolRunDetail; country?: string | null }) {
  // Gate the leave-the-page email promise on the backend's effective state.
  const me = useMe();
  if (run.status === 'queued') {
    return <QueuedBanner country={country} />;
  }
  if (run.status === 'running') {
    return (
      <>
      <AgentPollingPanel
        startedAt={run.started_at}
        workflowSteps={PROTOCOL_WORKFLOW_STEPS}
        stepSeconds={PROTOCOL_STEP_SECONDS}
        title={
          country ? (
            <>
              Protocol Drafting in Progress
              <span className="block mt-1.5 font-sans text-electric text-[22px] font-bold tracking-tight">
                {countryLabel(country)}
              </span>
            </>
          ) : (
            'Protocol Drafting in Progress'
          )
        }
        subtitle="Querying trial registries and current regulatory guidance via the REGARA AI agent. You can safely leave this page — the run continues on the server, and this link picks it back up."
      />
      {me.data?.protocol_run_email_enabled ? (
        <p className="text-[12.5px] text-muted text-center mt-3">
          You can leave this page we&apos;ll email you when the protocol is ready.
        </p>
      ) : me.data?.protocol_run_email_admin_enabled && me.data.agent_email_prefs?.protocol === false ? (
        <p className="text-[12.5px] text-muted text-center mt-3">
          Email notifications for Clinical Protocol are off —{' '}
          <Link to="/profile" className="underline">turn them on in Profile &amp; Settings</Link>.
        </p>
      ) : null}
      </>
    );
  }

  if (run.status === 'failed') {
    return (
      <DispatchFailedBanner
        country={country}
        message={friendlyErrorMessage(run.error_message)}
      />
    );
  }

  return <CompletedSection run={run} country={country} />;
}

/** Multi-country view. Polls every sibling run in the dispatch group and
 *  renders one tab per country. When the dispatch has a single child
 *  (today's single-country case), the shell auto-collapses to the legacy
 *  inline view. Mirrors the strategy-side ``DispatchView`` exactly. */
function DispatchView({
  groupId,
  initialRunId,
}: {
  groupId: string;
  initialRunId?: number | null;
}) {
  const { data, isLoading, isError } = useClinicalProtocolDispatchDetail(groupId);
  const shellData: DispatchData | undefined = data
    ? { dispatch_group_id: data.dispatch_group_id, runs: data.runs }
    : undefined;
  return (
    <DispatchTabsShell
      data={shellData}
      isLoading={isLoading}
      isError={isError}
      initialRunId={initialRunId}
      renderRunMeta={(runId) => <ProtocolRunMetaCard runId={runId} />}
      renderSingleRun={(runId) => <ProtocolSingleRun runId={runId} />}
      renderRunBody={(runId, country) => (
        <ProtocolRunBody runId={runId} country={country} />
      )}
    />
  );
}

function ProtocolRunMetaCard({ runId }: { runId: number }) {
  const { data: run } = useClinicalProtocolRun(runId);
  if (!run) return null;
  return <AgentRunCard run={run} agentLabel="Protocol run" />;
}

function ProtocolSingleRun({ runId }: { runId: number }) {
  const { data: run, isLoading, isError } = useClinicalProtocolRun(runId);
  if (isLoading) {
    return <RunLoadingSkeleton />;
  }
  if (isError || !run) {
    return <ErrorCard message="Could not load the run. Please refresh." />;
  }
  return (
    <>
      <AgentRunCard run={run} agentLabel="Protocol run" />
      <RunStateBody run={run} />
      {run.status === 'completed' && <ResultDisclaimer country={run.country} />}
    </>
  );
}

function ProtocolRunBody({
  runId,
  country,
}: {
  runId: number;
  country: string | null;
}) {
  const { data: run, isLoading, isError } = useClinicalProtocolRun(runId);
  if (isLoading) {
    return <RunLoadingSkeleton />;
  }
  if (isError || !run) {
    return <ErrorCard message="Could not load the run. Please refresh." />;
  }
  return (
    <>
      <RunStateBody run={run} country={country} />
      {run.status === 'completed' && <ResultDisclaimer country={run.country ?? country} />}
    </>
  );
}

function CompletedSection({
  run,
  country,
}: {
  run: ClinicalProtocolRunDetail;
  country?: string | null;
}) {
  const startedAt = parseServerDate(run.started_at);
  const completedAt = parseServerDate(run.completed_at);
  const durationSec =
    startedAt && completedAt
      ? Math.round((completedAt.getTime() - startedAt.getTime()) / 1000)
      : null;

  const response = run.protocol_response;

  return (
    <>
      {/* Run's own country first — the collapsed single-run view passes no
          tab country, and the banner scopes per market. */}
      <ProtocolStaleBanner
        productId={run.product_id}
        runId={run.id}
        runCompletedAt={run.completed_at}
        country={run.country ?? country ?? null}
      />
      <div className="mb-[22px] py-[18px] px-[22px] bg-state-success-bg border border-state-success-border rounded-xl">
        <div className="flex items-start gap-3">
          <CheckCircle className="size-6 text-state-success-accent shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3 className="m-0 mb-1.5 text-[17px] font-bold text-state-success-text">
              Protocol Complete
              {country && (
                <span className="block mt-1 text-navy text-[24px] font-bold tracking-tight">
                  {countryLabel(country)}
                </span>
              )}
            </h3>
            <p className="text-sm leading-[1.55] text-state-success-text/90 m-0">
              Completed {formatServerDateShort(run.completed_at)}
              {durationSec !== null && ` · ${formatDuration(durationSec)}`}
            </p>
          </div>
        </div>
      </div>

      {response ? (
        <ClinicalProtocolResultRenderer response={response} runId={run.id} />
      ) : (
        <div className="bg-state-error-bg border border-state-error-border rounded-2xl p-6">
          <div className="font-semibold text-state-error-text text-lg">
            Could not parse the protocol response
          </div>
          <p className="text-sm text-state-error-text mt-1">
            The run completed but the protocol payload was not delivered. Try generating
            a new protocol.
          </p>
        </div>
      )}
    </>
  );
}

/**
 * Renders the dispatch form (submission + sponsor type + Generate) when
 * we can resolve the product. If ``useProducts()`` doesn't return one
 * (404 / wrong owner / not yet loaded) we fall back to the friendly
 * "go back to the list" card. Same shape as clinical-reports' detail
 * page below the run-id branch.
 */
function DispatchOrMissing({ productId }: { productId: string }) {
  const { data: products, isLoading } = useProducts();
  if (isLoading) {
    return <RunLoadingSkeleton label="Loading product" />;
  }
  const numericId = Number(productId);
  const product = products?.find((p) => p.id === numericId) ?? null;
  if (!product) {
    return (
      <div className="bg-state-warning-bg border border-state-warning-border rounded-2xl p-8 text-state-warning-text">
        <div className="font-semibold text-lg">Product not found</div>
        <p className="text-sm mt-1">
          Product <code>#{productId}</code> either doesn&rsquo;t exist or
          isn&rsquo;t accessible to your account. Return to the list to
          pick a different product.
        </p>
        <Button variant="outline" asChild className="mt-4">
          <a href="/clinical-protocols">← Back to product list</a>
        </Button>
      </div>
    );
  }
  return <ClinicalProtocolDispatchForm product={product} />;
}

function ErrorCard({ message }: { message: string }) {
  return (
    <div className="bg-state-error-bg border border-state-error-border rounded-2xl p-8 text-state-error-text">
      {message}
    </div>
  );
}

function ResultDisclaimer({ country }: { country: string | null }) {
  const reviewBody = country === 'EU' ? 'ethics committee' : 'IRB';
  return (
    <footer className="mt-7 py-[18px] px-[22px] bg-state-warning-bg-soft border border-state-warning-border rounded-xl text-[#713f12] text-base leading-[1.6]">
      <strong className="block text-xs font-bold uppercase tracking-[0.22em] text-state-warning-text mb-1.5">
        Disclaimer.
      </strong>
      This is REGARA AI-drafted clinical-protocol decision-support, not legal or
      regulatory advice. Regulatory and {reviewBody} decisions rest with those bodies. Review every
      recommendation with a qualified regulatory professional before relying on it.
    </footer>
  );
}

function friendlyErrorMessage(raw: string | null): string {
  const msg = (raw ?? '').toString();
  if (!msg) {
    return 'The protocol generation did not complete. Try running it again from the product list.';
  }
  if (/no JSON|did not produce/i.test(msg)) {
    return 'The agent did not produce a valid protocol. Try starting a new run.';
  }
  if (/timeout|timed out/i.test(msg)) {
    return 'The protocol generation took too long to complete. Try starting a new run.';
  }
  if (/auth|api[_ ]?key|unauthor/i.test(msg)) {
    return 'The agent could not reach the protocol service. Please contact support.';
  }
  return 'Something went wrong while generating the protocol. Try starting a new run, or contact support if this keeps happening.';
}
